import { useCallback, useEffect } from 'react';

export interface IImageLazyLoaderProps {
  containerSelector: string;
  imageClassName?: string;
  loadingClassName?: string;
  isBackgroundImage?: boolean;
  srcDataAttr?: string;
  errorImagePath?: string;
  isReady: boolean;
  onLoaded?: () => void;
  fadeInDuration?: number;
}

export function ImageLazyLoader({
  containerSelector,
  imageClassName = 'image-lazy-load',
  loadingClassName = 'loading',
  srcDataAttr = 'src',
  isBackgroundImage,
  isReady,
  errorImagePath,
  onLoaded,
}: IImageLazyLoaderProps) {
  const loadImage = useCallback(
    (target: Element) => {
      const el = target as HTMLElement;
      // for fade-in effect:
      const src = el.dataset[srcDataAttr];
      el.classList.remove(imageClassName);
      const onLoad = () => {
        // add the background image after loaded and cached the image
        if (isBackgroundImage) el.style.backgroundImage = `url(${src})`;
        el.classList.remove(loadingClassName);
      };
      const onError = (e: Event) => {
        el.classList.remove(loadingClassName);
        if (isBackgroundImage) {
          el.style.backgroundImage = `url(${errorImagePath})`;
        } else {
          if (errorImagePath && e.target) (e.target as HTMLImageElement).src = errorImagePath;
        }
      };
      if (src) {
        let img: HTMLImageElement;
        if (isBackgroundImage) {
          img = new Image();
        } else {
          img = el as HTMLImageElement;
        }
        img.addEventListener('load', onLoad);
        img.addEventListener('error', onError);
        img.src = src;
      }
      return el;
    },
    [imageClassName, srcDataAttr, isBackgroundImage, errorImagePath, loadingClassName]
  );

  const getEls = useCallback(() => {
    const containerEl = document.querySelector(containerSelector) as HTMLElement;
    return containerEl
      ? { containerEl, els: containerEl.querySelectorAll(`.${imageClassName}`) }
      : { containerEl, els: [] };
  }, [containerSelector, imageClassName]);

  const loadImagesByIO = useCallback(() => {
    const { els } = getEls();
    if (els.length) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(loadImage(entry.target));
          }
        });
      });
      els.forEach((el: Element) => observer.observe(el));
    } else {
      if (onLoaded !== undefined) {
        onLoaded();
      }
    }
  }, [loadImage, getEls, onLoaded]);

  useEffect(() => {
    if (isReady) {
      if ('IntersectionObserver' in window) {
        loadImagesByIO();
      }
    }
  }, [isReady, loadImagesByIO]);

  return null;
}
